import end from "./end.js";
import join from "./init.js";
import launchUI from "./launch.js";
import debug from "./debug.js";
import { createLifecycle } from "./lifecycle.js";
import type { AcquireOptions } from "./lifecycle.js";

/**
 * The lifecycle machine is the single owner of client game lifecycle state.
 * Everything below is thin adapters between the public Game API, the socket
 * handlers, and the machine. See lifecycle.ts for the state diagram.
 */
const lifecycle = createLifecycle({
	join: (sessionId, opts) => {
		void join({ sessionId, ...opts });
	},
	launch: (sessionId) => launchUI(sessionId),
	teardown: (sessionId) => {
		void end(sessionId);
	},
	schedule: (fn) => setTimeout(fn, 0),
	cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
});

export interface GameAcquireOptions extends AcquireOptions {
	sessionId: string;
}

/**
 * Acquire the game for a mounting component. Safe to call from a React
 * useEffect with a plain `return () => Game.release(sessionId)` cleanup —
 * StrictMode remounts are absorbed by the machine, no deferred-cancel
 * dance needed at the call site.
 */
function acquire({ sessionId, ...opts }: GameAcquireOptions) {
	lifecycle.acquire(sessionId, opts);
}

function release(sessionId: string) {
	lifecycle.release(sessionId);
}

/** Back-compat alias for acquire. Prefer acquire/release in new code. */
async function init(opts: GameAcquireOptions) {
	acquire(opts);
	return opts.sessionId;
}

/** Route a game:launch socket event into the machine (idempotent). */
function launch(sessionId: string) {
	lifecycle.notifyLaunch(sessionId);
}

/** Route a session snapshot into the machine — launches if phase is "launched". */
function onSnapshot(sessionId: string, data: Record<string, unknown>) {
	lifecycle.onSnapshot(sessionId, data);
}

/** End the session (server game:end or explicit). Idempotent. */
function endGame(sessionId: string) {
	lifecycle.end(sessionId);
}

export { acquire, release, init, launch, onSnapshot, endGame as end, debug, lifecycle };
export default { acquire, release, init, launch, onSnapshot, end: endGame, debug, lifecycle };
